/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
import React, { useContext } from 'react';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import appConfig from 'app-config';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import PropTypes from 'prop-types';
import { MLDriverVehicleUtil } from 'ml-portals-util-react';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import metadata from './PolicySummaryDriversComponent.metadata.json5';
import messages from './PolicySummaryDriversComponent.messages';
import styles from './PolicySummaryDriversComponent.module.scss';
import { Button } from '@jutro/legacy/components';

const TAB_BREAKPOINT = 768;

function PolicySummaryDriversComponent(props) {
    const {
        model: policyCoverables,
        editDrivers,
        isPUP,
        gpaMode,
        isPolicyChange,
        isModernProduct
    } = props;
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const isQnB = appConfig.portalCode === 'QB';

    const getDrivers = () => {
        if (!policyCoverables.drivers) {
            return _.get(policyCoverables, 'vehicleOperators.children', []);
        }
        return _.get(policyCoverables, 'drivers.children', []);
    };
    const drivers = getDrivers();

    const {
        getFullName,
        isDriverHousehold
    } = MLDriverVehicleUtil();

    const getDriversOfSpecificType = (driverType) => {
        return _.filter(drivers, (driver) => {
            if ((driver.type_ML && _.get(driver, 'type_ML.value.code', '') === driverType) || (driver.driverType && _.get(driver, 'driverType.value.code', '') === driverType)) {
                if (!driver.person) {
                    driver.person = driver.contact;
                }
                return driver;
            }
        });
    };

    const householdMembers = isModernProduct ? drivers.filter((d) => isDriverHousehold(d.value)) : getDriversOfSpecificType('household');

    const ratedDrivers = getDriversOfSpecificType('rated');

    const excludedDrivers = getDriversOfSpecificType('excluded');

    const nonDrivers = [
        ...getDriversOfSpecificType('permitted'),     
        ...getDriversOfSpecificType('nondriver'),     
        ...getDriversOfSpecificType('outofhousehold') 
    ];

    const isMobile = () => {
        return window.innerWidth <= TAB_BREAKPOINT;
    };

    const formatName = (contact) => {
        if (!_.isUndefined(contact)) {
            const firstName = contact.firstName || contact.name;
            const middleName = contact.middleName || '';
            let displayName = _.capitalize(`${firstName} `) + _.capitalize(`${middleName} `) + _.capitalize(`${contact.lastName}`);

            if (contact.suffix) {
                const suffixTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('NameSuffix');
                const suffixName = suffixTypelist.getCode(contact.suffix).name;
                displayName += ', ';
                displayName += translator({
                    id: suffixName,
                    defaultMessage: suffixName
                });
            }
            return displayName;
        }
    };

    const getNameCell = (item) => {
        const contact = isPUP ? item.contact.value : item.person.value;
        return (
            <>
                <span className={styles.driverBoldCellValue}>{formatName(contact)}</span>
            </>
        )
    };

    const genderTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('GenderType');

    const getGenderCell = (item) => {
        const genderCode = isPUP ? _.get(item, "contact.gender.value.code") : _.get(item, "gender.value.code");
        if (_.isUndefined(genderCode)) {
            return <></>;
        }

        const gender = genderTypeList.getCode(genderCode).name;
        return (
            <>
                <span className={styles.driverBoldCellValue}>{translator({id: gender, defaultMessage: gender})}</span>
            </>
        ) 
    };

    const getDOBCell = (item) => {
        const dobVal = isPUP ? item.contact.dateOfBirth.value : item.dateOfBirth.value;
        const dateOfBirth = MLLocalDateUtil.localDate2Date(dobVal, true);
        return dateOfBirth;
    };

    const getMaritalStatusCell = (item) => {
        const maritalStatusCode =  isPUP ? _.get(item, 'contact.maritalStatus.value.code', undefined) : _.get(item, 'maritalStatus_ML.value.code', undefined);
        if (maritalStatusCode) {
            const maritalStatusTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('MaritalStatus');
            const maritalStatus = maritalStatusTypelist.getCode(maritalStatusCode).name;
            return translator({
                id: maritalStatus,
                defaultMessage: maritalStatus
            });
        }
        return '';
    };
    const getpfmCell = (item) => {
        if (isPUP) {
            return item.pfm.value;
        }
        
        if( item.pfmLevel_ML.value){
            const pfmcode = item.pfmLevel_ML.value.code;
            const pfmTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('PFMLevel_ML');
            const pfmLevel = pfmTypelist.getCode(pfmcode).name;
            return translator({
                id: pfmLevel,
                defaultMessage: pfmLevel
            });
        }
        return '';
        
    }

    const renderEditDriverAction = () => {
        if (!isPUP && !gpaMode && isPolicyChange && !isQnB) {
            return (
                <a className={styles.editDriversLink} onClick={editDrivers}>
                    {translator(messages.summaryEditDrivers)}
                </a>
            );
        }
    };

    const renderDriverTitle = () => {
        return (
            <h2 className="subsection-header">
                {translator(messages.summaryDrivers)}
            </h2>
        );
    };

    const generateOverrides = () => {
        const householdMembersOverrides = householdMembers.map((householdMember, index) => {
            return {
                [`householdMemberName${index}`]: {
                    content: getFullName(householdMember.person.value)
                }
            };
        });

        const excludedDriversOverride = excludedDrivers.map((excludedDriver, index) => {
            return {
                [`excludedDriverName${index}`]: {
                    content: getFullName(excludedDriver.person.value)
                }
            };
        });

        const nonDriverOverrides = nonDrivers.map((nonDriver, index) => {
            return {
                [`householdMemberName${index}`]: {
                    content: getFullName(nonDriver.person.value)
                }
            };
        });
        return Object.assign({}, ...householdMembersOverrides, ...excludedDriversOverride, ...nonDriverOverrides);
    };

    const resolvers = {
        resolveCallbackMap: {
            getNameCell: getNameCell,
            getGenderCell: getGenderCell,
            getDOBCell: getDOBCell,
            getMaritalStatusCell: getMaritalStatusCell,
            getpfmCell: getpfmCell
        }
    };

    const overrideProps = {
        driversComponent: {
            visible: drivers.length > 0
        },
        driversTable: {
            data: ratedDrivers,
            renderTitle: renderDriverTitle,
            renderTitleAction: renderEditDriverAction
        },
        houseHoldDriversContainer: {
            visible: householdMembers.length > 0 || excludedDrivers.length > 0 || nonDrivers.length > 0
        },
        householdMembersContainer: {
            visible: householdMembers.length > 0 || nonDrivers.length > 0
        },
        householdMemberIterable: {
            data: !(isPUP && isModernProduct) ? householdMembers : nonDrivers
        },
        householdMember: {
            content: isModernProduct ? translator(messages.nonDriversHeader) : translator(messages.houseHoldMemberHeader)
        },
        excludedDriversContainer: {
            visible: excludedDrivers.length > 0
        },
        exclusionDriverIterable: {
            data: excludedDrivers
        },
        pfmCell :{
            visible : gpaMode && !isModernProduct,
            header: isMobile() ? `${translator(messages.summaryDriverPfm)}:` :  translator(messages.summaryDriverPfm)
        },
        nameCell: {
            header: isMobile() ? `${translator(messages.summaryDriverName)}:` :  translator(messages.summaryDriverName)
        },
        genderCell: {
            header: isMobile() ? `${translator(messages.summaryDriverGender)}:` :  translator(messages.summaryDriverGender)
        },
        dobCell: {
            header: isMobile() ? `${translator(messages.summaryDriverDateOfBirth)}:` :  translator(messages.summaryDriverDateOfBirth)
        },
        maritalStatusCell: {
            header: isMobile() ? `${translator(messages.summaryDriverMaritalStatus)}:` :  translator(messages.summaryDriverMaritalStatus)
        },
        ...generateOverrides()
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={policyCoverables}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={styles}
        />
    );
}

PolicySummaryDriversComponent.propTypes = {
    model: PropTypes.shape([]).isRequired,
    editDrivers: PropTypes.func.isRequired,
    isPUP: PropTypes.bool,
    gpaMode: PropTypes.bool,
    isPolicyChange: PropTypes.bool,
    isModernProduct: PropTypes.bool
};

PolicySummaryDriversComponent.defaultProps = {
    isPUP: false,
    gpaMode: false,
    isPolicyChange: true,
    isModernProduct: false
};

export default PolicySummaryDriversComponent;
